var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
import { TransportError } from './framework/Transport';
export class VaddioCamera {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.cameraInfo = {};
        this.timeouts = new Timeouts();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.cameras = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                return {
                    name: this.cameraInfo[key].name,
                    hasCameraTracking: false,
                    hasPan: true,
                    hasTilt: true,
                    hasZoom: true,
                    location: 'near',
                };
            }),
            getKey: (config) => {
                if (!('id' in config)) {
                    this.log.error(`invalid camera config: ${JSON.stringify(config)}`);
                    return 'invalid';
                }
                return config.id.toString();
            },
            validate: (config) => __awaiter(this, void 0, void 0, function* () {
                if (!('id' in config))
                    return 'incompatible';
                const driverKey = config.id.toString();
                return this.cameraInfo[driverKey] === undefined ? 'missing' : 'ok';
            }),
        };
        this.cameraMove = {
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                const cameraInfo = this.cameraInfo[key];
                if (cameraInfo === undefined) {
                    this.log.error(`cameraMove: unknown key ${JSON.stringify(key)}`);
                    return value;
                }
                this.state.cameraPreset.update(undefined);
                if ('direction' in value)
                    cameraInfo.moveDirection = value.direction;
                if (value.action === 'start') {
                    cameraInfo.moveSpeed = 'slow';
                    yield this.sendCameraMove('start', cameraInfo, tags);
                }
                else if (value.action === 'stop') {
                    yield this.sendCameraMove('stop', cameraInfo, tags);
                }
                else if (value.action === 'repeat') {
                    if (((_a = cameraInfo.moveSpeed) !== null && _a !== void 0 ? _a : 'slow') === 'slow') {
                        // TODO: can we just start again instead of having to stop first
                        yield this.sendCameraMove('stop', cameraInfo, tags);
                        cameraInfo.moveSpeed = 'fast';
                        yield this.sendCameraMove('start', cameraInfo, tags);
                    }
                    else {
                        yield this.sendCameraMove('continue', cameraInfo, tags);
                    }
                }
                return value;
            }),
        };
        this.cameraPosition = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const cameraInfo = this.cameraInfo[key];
                if (cameraInfo === undefined) {
                    this.log.error(`cameraPosition: unknown key ${JSON.stringify(key)}`);
                    return { pan: 0, tilt: 0, zoom: 0 };
                }
                // The ? is not in the documentation, but we get no response without it.
                let pan = 0;
                let tilt = 0;
                let zoom = 0;
                let match = yield this.socket.send(`camera pan get ?`, /\dm([+-]?[\d\.]+)/i, tags);
                if (match) {
                    pan = parseInt(match[1], 10);
                }
                match = yield this.socket.send(`camera tilt get ?`, /\dm([+-]?[\d\.]+)/i, tags);
                if (match) {
                    tilt = parseInt(match[1], 10);
                }
                match = yield this.socket.send(`camera zoom get ?`, /\dm([+-]?[\d\.]+)/i, tags);
                if (match) {
                    zoom = parseInt(match[1], 10);
                }
                return { pan: pan, tilt: tilt, zoom: zoom };
            }),
        };
        this.cameraPreset = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.cameraPreset.value;
            }),
            set: (presetId, tags) => __awaiter(this, void 0, void 0, function* () {
                var _b, _c, _d;
                if (presetId === undefined)
                    return undefined;
                const preset = this.getPreset(presetId);
                if (preset === undefined) {
                    this.log.error(`preset "${presetId}" not found`);
                    return undefined;
                }
                const cameraInfo = this.cameraInfo[preset.cameraId.toString()];
                if (cameraInfo === undefined) {
                    this.log.error(`camera not found for preset "${presetId}"`);
                    return undefined;
                }
                // set pan/tilt - no_wait allows response to come back before movement occurs
                yield this.socket.send(`camera pan set ${(_b = preset.pan) !== null && _b !== void 0 ? _b : 0} no_wait`, /OK|out of bounds/i, tags);
                yield this.socket.send(`camera tilt set ${(_c = preset.tilt) !== null && _c !== void 0 ? _c : 0} no_wait`, /OK|out of bounds/i, tags);
                yield this.socket.send(`camera zoom set ${(_d = preset.zoom) !== null && _d !== void 0 ? _d : 1} no_wait`, /OK|out of bounds/i, tags);
                return presetId;
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`version`, /System Version\s*(.+)/i, tags);
                if (match)
                    return match[1];
                return 'unknown';
            }),
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                // Vaddio documentation indicates no ? is required in the query command however when
                // testing remotely with network tools, there was no response without a ?. We try it
                // both ways until we have a camera to confirm.
                try {
                    const match = yield this.socket.send(`camera standby get`, /^standby:\s*(on|off)/i, tags);
                    return match ? match[1].toLowerCase() === 'off' : this.state.power.value;
                }
                catch (error) {
                    if (!(error instanceof TransportError)) {
                        this.log.error(error);
                    }
                    const match = yield this.socket.send(`camera standby get ?`, /^standby:\s*(on|off)/i, tags);
                    return match ? match[1].toLowerCase() === 'off' : this.state.power.value;
                }
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                this.state.busy.update(true);
                const match = yield this.socket.send(`camera standby ${power ? 'off' : 'on'}`, /^(OK|error)/i, tags);
                this.state.busy.update(false);
                return match ? match[1].toLowerCase() === 'ok' : this.state.power.value;
            }),
        };
        // polling
        this.polls = [{ intervalMs: 3000, controls: ['power'] }];
    }
    // phase callbacks
    onConfigure(config) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.cameraInfo = {};
            this.timeouts.clearAll();
            for (const cameraConfig of Object.values((_a = config.cameras) !== null && _a !== void 0 ? _a : {})) {
                if (!('id' in cameraConfig)) {
                    this.log.error(`invalid camera config: ${JSON.stringify(cameraConfig)}`);
                    continue;
                }
                const driverKey = cameraConfig.id.toString();
                this.cameraInfo[driverKey] = Object.assign(Object.assign({}, cameraConfig), { hasPower: true });
            }
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials, config) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield CommandSocketInitializer.initialize(this.socket, [
                {
                    errorCode: 'verifyFailed',
                    unterminatedRegExp: /Login:/i,
                    next: /Login:/i,
                },
                {
                    errorCode: 'authFailed',
                    send: (cred) => {
                        var _a;
                        return (_a = cred.username) !== null && _a !== void 0 ? _a : 'admin';
                    },
                    unterminatedRegExp: /Password:/i,
                    next: /Password:/i,
                    fail: /Login incorrect/i,
                },
                {
                    errorCode: 'authFailed',
                    send: (cred) => {
                        var _a;
                        return (_a = cred.password) !== null && _a !== void 0 ? _a : 'admin';
                    },
                    succeed: /OK/,
                    next: /Vaddio\s+Interactive\s+Shell|Welcome|Last login:/i,
                    fail: /Login incorrect/i,
                },
            ], credentials);
            return result;
        });
    }
    // other class methods
    getPreset(presetId) {
        var _a;
        for (const [configId, cameraInfo] of Object.entries(this.cameraInfo)) {
            const preset = (_a = cameraInfo.presets) === null || _a === void 0 ? void 0 : _a[presetId];
            if (preset !== undefined) {
                return Object.assign(Object.assign({}, preset), { configId, presetId, cameraId: cameraInfo.id });
            }
        }
        return undefined;
    }
    sendCameraMove(action, cameraInfo, tags) {
        var _a, _b, _c, _d, _e, _f;
        return __awaiter(this, void 0, void 0, function* () {
            let cmd = '';
            // Camera speed ranges match those for Visca cameras.
            const panSpeed = cameraInfo.moveSpeed === 'slow'
                ? (_a = cameraInfo.panSpeedSlow) !== null && _a !== void 0 ? _a : 3
                : (_b = cameraInfo.panSpeedFast) !== null && _b !== void 0 ? _b : 8;
            const tiltSpeed = cameraInfo.moveSpeed === 'slow'
                ? (_c = cameraInfo.tiltSpeedSlow) !== null && _c !== void 0 ? _c : 1
                : (_d = cameraInfo.tiltSpeedFast) !== null && _d !== void 0 ? _d : 8;
            const zoomSpeed = cameraInfo.moveSpeed === 'slow'
                ? (_e = cameraInfo.zoomSpeedSlow) !== null && _e !== void 0 ? _e : 1
                : (_f = cameraInfo.zoomSpeedFast) !== null && _f !== void 0 ? _f : 3;
            const direction = cameraInfo.moveDirection === 'left'
                ? cameraInfo.invertPan
                    ? 'right'
                    : 'left'
                : cameraInfo.moveDirection === 'right'
                    ? cameraInfo.invertPan
                        ? 'left'
                        : 'right'
                    : cameraInfo.moveDirection;
            if (action === 'start') {
                if (direction === 'left') {
                    cmd = `camera pan left ${panSpeed}`;
                }
                else if (direction === 'right') {
                    cmd = `camera pan right ${panSpeed}`;
                }
                else if (direction === 'down') {
                    cmd = `camera tilt down ${tiltSpeed}`;
                }
                else if (direction === 'up') {
                    cmd = `camera tilt up ${tiltSpeed}`;
                }
                else if (direction === 'out') {
                    cmd = `camera zoom out ${zoomSpeed}`;
                }
                else if (direction === 'in') {
                    cmd = `camera zoom in ${zoomSpeed}`;
                }
                else {
                    this.log.error(`invalid move direction: ${cameraInfo.moveDirection}`);
                    return;
                }
            }
            else if (action === 'stop') {
                if (['left', 'right'].includes(direction !== null && direction !== void 0 ? direction : 'none')) {
                    cmd = `camera pan stop`;
                }
                else if (['up', 'down'].includes(direction !== null && direction !== void 0 ? direction : 'none')) {
                    cmd = `camera tilt stop`;
                }
                else if (['in', 'out'].includes(direction !== null && direction !== void 0 ? direction : 'none')) {
                    cmd = `camera zoom stop`;
                }
                else {
                    this.log.error(`invalid move direction: ${cameraInfo.moveDirection}`);
                    return;
                }
            }
            else if (action === 'continue') {
                // nop
            }
            if (cameraInfo.moveTimeout) {
                cameraInfo.moveTimeout.clear();
                cameraInfo.moveTimeout = undefined;
            }
            if (cmd !== '') {
                yield this.socket.send(cmd, /OK|ERROR/i, tags);
            }
            if (action === 'start' || action === 'continue') {
                cameraInfo.moveTimeout = this.timeouts.set(() => __awaiter(this, void 0, void 0, function* () {
                    cameraInfo.moveTimeout = undefined;
                    this.log.error(`camera move timeout for camera id ${cameraInfo.id}`);
                    yield this.sendCameraMove('stop', cameraInfo, tags);
                }), 1000);
            }
        });
    }
}
// static properties
VaddioCamera.driverType = 'socketDriver';
VaddioCamera.key = '-NQB4XlzfVDZvy8eXZKD';
VaddioCamera.socketOptions = [
    {
        commandExtractor: {
            terminators: ['\r', '\n'],
        },
        sendTerminator: '\r',
        port: 23,
    },
];
